import { useState } from "react";
import {
  Users,
  Calendar,
  Video,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Heart,
  Accessibility,
  BookOpen,
  ArrowRight,
  Clock,
  MapPin,
  User,
  Mail,
  Building2,
  Check,
  Star,
  Play,
  Download,
  Link,
  Globe,
  Zap,
  Brain,
  Target,
  MessageCircle,
  Share,
  CalendarPlus,
  Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Demo data
const COMMUNITY = {
  name: "Phygital Community",
  tagline: "Where creativity meets analytics",
  description: "A community for creators, founders, and curious learners exploring the intersection of data, design, and digital culture.",
  logo: "🌙",
  members: 847,
  events: 12
};

const EVENT = {
  name: "Front Row",
  subtitle: "Front Row Seat to Building a Digital Community",
  date: "January 13, 2026",
  time: "12:00 PM PST",
  type: "URL",
  typeLabel: "Online Workshop",
  duration: "90 minutes",
  zoomLink: "https://zoom.us/j/example",
  spots: 50,
  registered: 34
};

const INSTRUCTOR = {
  name: "Naomi",
  title: "Founder of Phygital",
  focus: "Building creative and analytical communities",
  bio: "Naomi is a community architect who helps creators and founders build meaningful digital spaces. She believes in the power of combining creativity with data-driven insights.",
  photo: null, // Would be Cloudinary URL
  socials: {
    instagram: "@datadreaming",
    website: "datadreaming.co"
  }
};

const ITINERARY = [
  { time: "0:00", title: "Intro", description: "Music moment with Tap In", icon: Play },
  { time: "0:05", title: "Welcome", description: "Community overview & introductions", icon: Users },
  { time: "0:15", title: "Workshop", description: "Building a Digital Community", icon: Brain },
  { time: "0:50", title: "Discussion", description: "Community Q&A", icon: MessageCircle },
  { time: "1:20", title: "Outro", description: "Music moment + next steps", icon: Star }
];

const RESOURCES = [
  { name: "Emergent Builder Tools", type: "Guide", icon: Zap },
  { name: "AI Prototyping Resources", type: "Collection", icon: Brain },
  { name: "Community Building Framework", type: "Template", icon: Target },
  { name: "Design Tools Directory", type: "Resource", icon: Sparkles }
];

const AUDIENCE_OPTIONS = [
  "Student", "Founder", "Creator", "Developer", 
  "Designer", "Operator", "Curious Learner"
];

const INTEREST_OPTIONS = [
  "AI Tools", "Creative Technology", "Entrepreneurship", 
  "Product Building", "Community Building", "Web Design",
  "Brand Strategy", "Marketing", "Startup Strategy",
  "Creator Economy", "Fashion Tech", "Digital Culture"
];

const ACCESSIBILITY_OPTIONS = [
  { id: "captions", label: "Captions Preferred", icon: "💬" },
  { id: "recording", label: "Recording Access", icon: "🎥" },
  { id: "camera_optional", label: "Camera Optional", icon: "📷" },
  { id: "low_bandwidth", label: "Low Bandwidth Option", icon: "📶" },
  { id: "step_by_step", label: "Step-by-Step Instructions", icon: "📝" },
  { id: "visual_slides", label: "Visual Slides Preferred", icon: "🖼️" }
];

export default function CommunityDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAudience, setSelectedAudience] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedAccessibility, setSelectedAccessibility] = useState([]);

  const steps = [
    { id: "discover", label: "Discover Event", icon: Globe },
    { id: "rsvp", label: "RSVP", icon: Calendar },
    { id: "intake", label: "Insight Intake", icon: Heart },
    { id: "confirmation", label: "Confirmation", icon: Check }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-xl">
                {COMMUNITY.logo}
              </div>
              <div>
                <h1 className="font-bold text-white">Phygital Communities</h1>
                <p className="text-xs text-white/60">Instructor Pilot Demo</p>
              </div>
            </div>
            <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">
              URL Event
            </Badge>
          </div>
        </div>
      </header>

      {/* User Flow Progress */}
      <div className="bg-black/30 border-b border-white/10 py-4">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-center gap-2">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isPast = idx < currentStep;
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(idx)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    isActive 
                      ? "bg-violet-500 text-white" 
                      : isPast
                        ? "bg-violet-500/30 text-violet-300"
                        : "bg-white/5 text-white/40 hover:bg-white/10"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{step.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        
        {/* Step 0: Discover Event */}
        {currentStep === 0 && (
          <div className="space-y-8 animate-in fade-in duration-500">
            {/* Community Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
                <span className="text-2xl">{COMMUNITY.logo}</span>
                <span className="text-white/80 font-medium">{COMMUNITY.name}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {EVENT.name}
              </h2>
              <p className="text-xl text-white/60 max-w-2xl mx-auto">
                {EVENT.subtitle}
              </p>
            </div>

            {/* Event Card */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl max-w-2xl mx-auto overflow-hidden">
              <div className="bg-gradient-to-r from-violet-500/20 to-purple-500/20 px-6 py-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    <Video className="w-3 h-3 mr-1" />
                    {EVENT.typeLabel}
                  </Badge>
                  <span className="text-white/60 text-sm">
                    {EVENT.registered}/{EVENT.spots} registered
                  </span>
                </div>
              </div>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 text-white/80">
                    <Calendar className="w-5 h-5 text-violet-400" />
                    <div>
                      <p className="font-medium">{EVENT.date}</p>
                      <p className="text-sm text-white/50">{EVENT.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-white/80">
                    <Clock className="w-5 h-5 text-violet-400" />
                    <div>
                      <p className="font-medium">{EVENT.duration}</p>
                      <p className="text-sm text-white/50">Interactive session</p>
                    </div>
                  </div>
                </div>

                <p className="text-white/70 leading-relaxed">
                  Join us for an immersive workshop on building and nurturing digital communities. 
                  Learn frameworks, share insights, and connect with fellow community builders.
                </p>

                <Button 
                  onClick={() => setCurrentStep(1)}
                  className="w-full h-14 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold text-lg rounded-xl"
                >
                  RSVP Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Meet Your Instructor Preview */}
            <div className="max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-white/80 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-violet-400" />
                Meet Your Instructor
              </h3>
              <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white flex-shrink-0">
                      N
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white">{INSTRUCTOR.name}</h4>
                      <p className="text-violet-400 font-medium">{INSTRUCTOR.title}</p>
                      <p className="text-white/60 mt-2 text-sm leading-relaxed">
                        {INSTRUCTOR.bio}
                      </p>
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-white/40 text-sm flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {INSTRUCTOR.socials.website}
                        </span>
                        <span className="text-white/40 text-sm flex items-center gap-1">
                          {INSTRUCTOR.socials.instagram}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 1: RSVP */}
        {currentStep === 1 && (
          <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-500">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-2">Reserve Your Spot</h2>
              <p className="text-white/60">Quick signup to join {EVENT.name}</p>
            </div>

            <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
              <CardContent className="p-8 space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-white/80 text-sm font-medium mb-2 block">Name</label>
                    <input 
                      type="text" 
                      placeholder="Your name"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-violet-500"
                    />
                  </div>
                  <div>
                    <label className="text-white/80 text-sm font-medium mb-2 block">Email</label>
                    <input 
                      type="email" 
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-violet-500"
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => setCurrentStep(2)}
                  className="w-full h-14 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold text-lg rounded-xl"
                >
                  Continue to Preferences
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Insight Intake */}
        {currentStep === 2 && (
          <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-2">Help Us Personalize</h2>
              <p className="text-white/60">Share a bit about yourself (optional but helpful)</p>
            </div>

            {/* Audience */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-violet-400" />
                  I am a...
                </h3>
                <div className="flex flex-wrap gap-2">
                  {AUDIENCE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedAudience(prev => 
                          prev.includes(option) 
                            ? prev.filter(o => o !== option)
                            : [...prev, option]
                        );
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedAudience.includes(option)
                          ? "bg-violet-500 text-white"
                          : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Interests */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" />
                  Interests
                </h3>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedInterests(prev => 
                          prev.includes(option) 
                            ? prev.filter(o => o !== option)
                            : [...prev, option]
                        );
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedInterests.includes(option)
                          ? "bg-pink-500 text-white"
                          : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Accessibility */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Accessibility className="w-5 h-5 text-emerald-400" />
                  Accessibility Preferences
                </h3>
                <p className="text-white/50 text-sm mb-4">Help us make this event inclusive for you</p>
                <div className="grid grid-cols-2 gap-3">
                  {ACCESSIBILITY_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        setSelectedAccessibility(prev => 
                          prev.includes(option.id) 
                            ? prev.filter(o => o !== option.id)
                            : [...prev, option.id]
                        );
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left ${
                        selectedAccessibility.includes(option.id)
                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                          : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                      }`}
                    >
                      <span className="text-lg">{option.icon}</span>
                      {option.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button 
              onClick={() => setCurrentStep(3)}
              className="w-full h-14 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold text-lg rounded-xl"
            >
              Complete RSVP
              <Check className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {currentStep === 3 && (
          <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500">
            {/* Success Header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">You're In!</h2>
              <p className="text-white/60">See you at {EVENT.name} on {EVENT.date}</p>
            </div>

            {/* Event Details Card */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl overflow-hidden">
              <div className="bg-gradient-to-r from-violet-500/20 to-purple-500/20 px-6 py-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{COMMUNITY.logo}</span>
                    <div>
                      <h3 className="font-bold text-white">{EVENT.name}</h3>
                      <p className="text-white/60 text-sm">{COMMUNITY.name}</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    <Video className="w-3 h-3 mr-1" />
                    {EVENT.typeLabel}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-6 space-y-6">
                {/* Zoom Link */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                        <Video className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">Join Zoom Meeting</p>
                        <p className="text-blue-300 text-sm">{EVENT.date} at {EVENT.time}</p>
                      </div>
                    </div>
                    <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Join
                    </Button>
                  </div>
                </div>

                {/* Calendar Buttons */}
                <div>
                  <h4 className="text-white/80 font-medium mb-3 flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4" />
                    Add to Calendar
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10">
                      Google Calendar
                    </Button>
                    <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10">
                      Apple Calendar
                    </Button>
                    <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10">
                      Outlook
                    </Button>
                    <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Itinerary */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-violet-400" />
                  Event Itinerary
                </h3>
                <div className="space-y-3">
                  {ITINERARY.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <div key={idx} className="flex items-start gap-4 p-3 rounded-xl bg-white/5 border border-white/5">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-violet-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-white/40 text-sm font-mono">{item.time}</span>
                            <span className="font-semibold text-white">{item.title}</span>
                          </div>
                          <p className="text-white/60 text-sm">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Resources */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-amber-400" />
                  Resources & Brain Food
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {RESOURCES.map((resource, idx) => {
                    const Icon = resource.icon;
                    return (
                      <button 
                        key={idx}
                        className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-left group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-amber-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white group-hover:text-amber-300 transition-colors">{resource.name}</p>
                          <p className="text-white/40 text-xs">{resource.type}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60" />
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Meet Your Instructor */}
            <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/20 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  Meet Your Instructor
                </h3>
                <div className="flex items-start gap-5">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-4xl font-bold text-white flex-shrink-0 shadow-lg shadow-violet-500/25">
                    N
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-white">{INSTRUCTOR.name}</h4>
                    <p className="text-violet-400 font-medium mb-3">{INSTRUCTOR.title}</p>
                    <p className="text-white/70 leading-relaxed mb-4">
                      {INSTRUCTOR.bio}
                    </p>
                    <div className="flex items-center gap-4">
                      <Badge className="bg-white/10 text-white/70 border-white/20">
                        <Globe className="w-3 h-3 mr-1" />
                        {INSTRUCTOR.socials.website}
                      </Badge>
                      <Badge className="bg-white/10 text-white/70 border-white/20">
                        {INSTRUCTOR.socials.instagram}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Share CTA */}
            <div className="text-center space-y-4">
              <p className="text-white/60">Excited? Invite a friend!</p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10">
                  <Share className="w-4 h-4 mr-2" />
                  Share Event
                </Button>
                <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10">
                  <Link className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Flow Diagram */}
        <div className="mt-16 pt-12 border-t border-white/10">
          <h3 className="text-center text-white/50 text-sm font-medium mb-6">USER FLOW</h3>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {[
              { label: "Discover Event", color: "bg-violet-500" },
              { label: "→", color: "text-white/30" },
              { label: "RSVP", color: "bg-blue-500" },
              { label: "→", color: "text-white/30" },
              { label: "Insight Intake", color: "bg-pink-500" },
              { label: "→", color: "text-white/30" },
              { label: "Confirmation", color: "bg-emerald-500" },
              { label: "→", color: "text-white/30" },
              { label: "Attend Event", color: "bg-amber-500" },
              { label: "→", color: "text-white/30" },
              { label: "Host Insights", color: "bg-purple-500" }
            ].map((item, idx) => (
              item.label === "→" ? (
                <span key={idx} className="text-white/30 text-xl">→</span>
              ) : (
                <span 
                  key={idx} 
                  className={`${item.color} px-3 py-1.5 rounded-full text-white text-sm font-medium`}
                >
                  {item.label}
                </span>
              )
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white/40 text-sm">
            Phygital Communities × Instructor Pilot Demo
          </p>
          <p className="text-white/30 text-xs mt-1">
            Phygital Community
          </p>
        </div>
      </footer>
    </div>
  );
}
